import { request } from 'api';
import { endpoints } from 'api/endpoints';
import { ReactComponent as MoreIcon } from 'assets/chat-more.svg';
import { ReactComponent as CheckAll } from 'assets/check-all.svg';
import { ChatIcon, UploadChatFileIcon } from 'assets/icons';
import { ChatEntityType } from 'enums/chatEntityType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import useMediaQuery from 'hooks/useMediaQuery';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getProfileID } from 'reduxStore/reducers/ProfileSlice/selectors/getProfileID';
import { getTimezone } from 'reduxStore/reducers/ProfileSlice/selectors/getTimezone';
import { ChatMembershipV2, ChatMessageV2, ChatUser, OrderFile } from 'types';
import { DropDown } from 'ui/DropDown';
import { getDateWithTimezone } from 'utils/helpers';
import { IS_SMALL_DEVICE } from 'utils/settings';
import { OpenedChat } from './OpenedChat';
import { ChatMessageWithFilesV2, ChatProps, ChatsWithFilesV2 } from './types';

const Chat = ({
  orderId,
  entityType,
  isMessageCanSent = true,
  customId,
  orderStatus,
}: ChatProps) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<{
    chats: ChatsWithFilesV2 | null;
    messages: ChatMessageWithFilesV2[];
    members: ChatMembershipV2[];
    lastMessage: ChatMessageV2 | null;
  }>({
    chats: null,
    messages: [],
    members: [],
    lastMessage: null,
  });
  const [notViewedMessages, setNotViewedMessages] = useState<number>(0);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const timeZone = useAppSelector(getTimezone);
  const currentUserId = useAppSelector(getProfileID);
  const [uploadedFiles, setUploadedFiles] = useState<OrderFile[] | undefined>(undefined);
  const dispatch = useAppDispatch();
  const isSmall = useMediaQuery(IS_SMALL_DEVICE);

  // Метод для пометки сообщения "прочитанным"
  const chatViewMessage = async (orderId: number, messageId: number) => {
    const viewMessageResponse = await request<ChatsWithFilesV2>(
      endpoints.CHAT_ORDER_VIEW_MESSAGE.type,
      endpoints.CHAT_ORDER_VIEW_MESSAGE.url(orderId),
      {
        data: { messageId },
      }
    );
    if (viewMessageResponse.status === 200) {
      setAll(viewMessageResponse.data);
    }
  };

  useEffect(() => {
    request<ChatsWithFilesV2>(endpoints.CHAT_ORDER_GET.type, endpoints.CHAT_ORDER_GET.url(orderId))
      .then((chatData) => {
        if (chatData.status === 200) {
          setAll(chatData.data);
        } else {
          setAll(null);
        }
      })
      .catch((error) => {
        setAll(null);
      });
  }, [orderId]);

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView();
    }
  }, [open, state.messages, uploadedFiles, notViewedMessages, setAll]);

  useEffect(() => {
    const { lastMessage } = state;
    let enabled = true;
    if (notViewedMessages > 0 && enabled) {
      lastMessage && chatViewMessage(orderId, lastMessage.id);
      enabled = false;
    }
  }, [open]);

  useEffect(() => {
    if (state.chats && currentUserId) {
      const index = state.members.findIndex((member) => member.userId === +currentUserId);
      if (index > -1) {
        const chatMember = state.members[index];
        setNotViewedMessages(chatMember.notViewedMessages);
      } else {
        setNotViewedMessages(0);
      }
    }
  }, [state.chats, state.members, currentUserId]);

  const UserAvatar = ({ user, showAvatar }: { user: ChatUser | null; showAvatar: boolean }) => {
    if (!user || !showAvatar) {
      return null;
    }
    const initials = user.lastName.charAt(0) + user.firstName.charAt(0);
    return (
      <span className="self-start rounded-full bg-primary-60 px-[10px] py-2 text-base text-white">
        {initials.toUpperCase()}
      </span>
    );
  };

  const Message = ({
    userId,
    content,
    created,
    files,
  }: {
    userId: number;
    content: string;
    created: string;
    files?: OrderFile[];
  }) => {
    const { members } = state;
    const messageClasses = {
      currentUser: {
        wrapper: 'justify-end mr-1',
        bg: 'bg-primary-60 rounded-t-lg rounded-bl-lg',
        time: 'justify-end',
        text: '!text-white',
      },
      anotherUser: {
        wrapper: 'gap-2',
        bg: 'bg-gray-light rounded-t-lg rounded-br-lg',
        time: 'justify-start',
        text: '!text-text-100',
      },
      systemUser: {
        wrapper: 'justify-center',
        bg: 'bg-primary-60 rounded-lg',
        time: 'justify-end',
        text: '!text-white',
      },
    };
    const index = members.findIndex((member) => member.userId === userId);
    const chatMember = members[index];
    const user = chatMember?.user;
    const isCurrentUserMessage = !!userId && !!currentUserId && +currentUserId === userId;
    const showAvatar = !isCurrentUserMessage;

    const bg = isCurrentUserMessage ? messageClasses.currentUser.bg : messageClasses.anotherUser.bg;
    const wrapper = isCurrentUserMessage
      ? messageClasses.currentUser.wrapper
      : messageClasses.anotherUser.wrapper;
    const time = isCurrentUserMessage
      ? messageClasses.currentUser.time
      : messageClasses.anotherUser.time;
    const text = isCurrentUserMessage
      ? messageClasses.currentUser.text
      : messageClasses.anotherUser.text;
    return (
      <>
        <div className={`message flex ${wrapper}`}>
          <UserAvatar user={user} showAvatar={showAvatar} />
          <div className={`p-2 ${bg} flex w-72 flex-col`}>
            <p className={`leading-cz mb-1 flex-1 break-all ${text} `}>
              {user && entityType === ChatEntityType.ModerationRequestChat && (
                <span className="mb-1 block">{user.firstName}</span>
              )}
              {content
                .trim()
                .split('\n')
                .map((str, index) => (
                  <Fragment key={index}>
                    {str}
                    <br />
                  </Fragment>
                ))}
              {files &&
                files.map((file) => {
                  return (
                    <div
                      className="flex items-start justify-between gap-2 py-2"
                      key={file.downloadFileIdHash}
                    >
                      <div className="flex items-center gap-2">
                        <UploadChatFileIcon className="flex-shrink-0" />
                        <div>
                          <p className={`body-normal mb-1 ${text}`}>{file.fileName}</p>
                          <p className="callout-paragraphs-normal">
                            {file.fileName.split('.')[1].toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <div>
                        <DropDown
                          buttonMode={false}
                          button={<MoreIcon className="ml-1 mt-2 cursor-pointer" />}
                          menuItems={[]}
                          file={file}
                        />
                      </div>
                    </div>
                  );
                })}
            </p>
            <p>
              <span
                className={`flex ${time} leading-cz callout-paragraphs-normal items-end gap-0.5 ${text}`}
              >
                {getDateWithTimezone(created, timeZone, 'HH:mm')}
                {isCurrentUserMessage && <CheckAll />}
              </span>
            </p>
          </div>
        </div>
      </>
    );
  };

  function setAll(entity: ChatsWithFilesV2 | null) {
    if (!entity) {
      setState({
        chats: null,
        messages: [],
        members: [],
        lastMessage: null,
      });
    } else {
      setState({
        chats: entity,
        messages: entity.messages,
        members: entity.memberships,
        lastMessage: entity.messages[entity.messages.length - 1],
      });
    }
  }

  const openChatHandler = () => {
    dispatch(
      modalActions.handleOpenModal({
        content: (
          <OpenedChat
            state={state}
            uploadedFiles={uploadedFiles}
            orderId={orderId}
            setAll={setAll}
            currentUserId={currentUserId}
            isSmall={isSmall}
            setOpen={setOpen}
            customId={customId}
            entityType={entityType}
            timeZone={timeZone}
            isMessageCanSent={isMessageCanSent}
            lastMessageRef={lastMessageRef}
            setUploadedFiles={setUploadedFiles}
          />
        ),
        options: {
          title: customId ? `Чат по заявке №${customId}` : `Чат по заявке`,
          withCloseButton: true,
          withBackButton: false,
          contentClassName: 'overflow-x-hidden',
        },
      })
    );
  };

  if (!open) {
    if (orderStatus === 4 || orderStatus === 1) {
      return null;
    }
    if (state.chats) {
      return (
        <SmallChatPanel
          message={notViewedMessages}
          onClick={isSmall ? openChatHandler : () => setOpen(true)}
        />
      );
    } else {
      return <SmallChatPanel onClick={isSmall ? openChatHandler : () => setOpen(true)} />;
    }
  } else {
    return (
      <OpenedChat
        state={state}
        uploadedFiles={uploadedFiles}
        orderId={orderId}
        setAll={setAll}
        currentUserId={currentUserId}
        isSmall={isSmall}
        setOpen={setOpen}
        customId={customId}
        entityType={entityType}
        timeZone={timeZone}
        isMessageCanSent={isMessageCanSent}
        lastMessageRef={lastMessageRef}
        setUploadedFiles={setUploadedFiles}
      />
    );
  }
};

const SmallChatPanel = ({
  onClick,
  message = null,
}: {
  onClick: () => void;
  message?: number | null;
}) => {
  return (
    <div
      className="w-fit cursor-pointer self-end rounded-tl-[20px] py-3 pl-3 pr-2 md:rounded-bl-[20px]"
      onClick={onClick}
    >
      <div className="flex items-center gap-2 text-white">
        {typeof message === 'number' ? (
          <>
            <ChatIcon className="relative text-primary-60 hover:text-primary-80 active:text-primary-100" />
            {message && (
              <span className="absolute right-3 top-5 rounded-xl bg-danger px-2 font-roboto text-[10px] leading-4">
                +{message}
              </span>
            )}
          </>
        ) : (
          <ChatIcon className="text-primary-60 hover:text-primary-80 active:text-primary-100" />
        )}
      </div>
    </div>
  );
};

export const ChatOrder = memo(Chat);
